<template>
  <!-- Карточка точки (вершина, перевал, родник, стоянка...): ?point=<id>.
       Шапка не прокручивается: «Назад», иконка типа, название, тип словом (у
       перевала - с категорией), высота; крестик. На мобильном (compact) шапка -
       зона перетаскивания шторки MobilePointSheet. «Назад» и крестик только
       сообщают наверх - что они значат, решает useSearchNavigation. -->
  <div class="flex flex-col h-full overflow-hidden bg-[hsl(var(--card))]">
    <div
      ref="headerRef"
      data-point-header
      class="shrink-0 px-4 pt-2 pb-3 bg-sheet border-b-[1.5px] border-border"
      :style="compact ? 'touch-action: none' : undefined"
      v-bind="compact ? { 'data-sheet-drag-handle': true } : {}"
    >
      <div class="flex items-center gap-2">
        <button
          type="button"
          data-card-back
          class="shrink-0 w-9 h-9 flex items-center justify-center border border-border rounded-md bg-sheet hover:bg-paper2"
          :aria-label="t('point.back')"
          @click="$emit('back')"
        >
          <ArrowLeft class="w-4 h-4" aria-hidden="true" />
        </button>
        <component :is="icon" v-if="hit" class="w-5 h-5 shrink-0 text-forest" aria-hidden="true" />
        <div class="flex-1 min-w-0">
          <template v-if="hit">
            <h2 class="text-[19px] leading-tight line-clamp-2">{{ title }}</h2>
            <p data-point-subtitle class="text-[12px] text-ink2 truncate">{{ subtitle }}</p>
          </template>
          <div v-else-if="state === 'loading'" aria-hidden="true" class="space-y-1.5">
            <div class="h-4 w-2/3 rounded bg-[hsl(var(--muted))] animate-pulse" />
            <div class="h-3 w-1/3 rounded bg-[hsl(var(--muted)/0.7)] animate-pulse" />
          </div>
        </div>
        <button
          type="button"
          data-card-close
          class="shrink-0 w-9 h-9 flex items-center justify-center rounded-md text-ink2 hover:bg-paper2"
          :aria-label="t('point.close')"
          @click="$emit('close')"
        >
          <X class="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto">
      <div v-if="state === 'loading'" data-point-loading class="px-4 pt-3 space-y-2" :aria-busy="true">
        <span class="sr-only" role="status">{{ t('point.loading') }}</span>
        <div class="h-3 w-1/2 rounded bg-[hsl(var(--muted))] animate-pulse" aria-hidden="true" />
        <div class="h-8 w-3/4 rounded bg-[hsl(var(--muted)/0.7)] animate-pulse" aria-hidden="true" />
      </div>

      <div v-else-if="state === 'notFound'" data-point-not-found class="px-6 py-8 text-center">
        <p class="text-[14px] font-semibold">{{ t('point.notFound') }}</p>
        <p class="mt-1 text-[12px] text-ink2">{{ t('point.notFoundHint') }}</p>
      </div>

      <div v-else-if="state === 'error'" data-point-error role="alert" class="mx-3 my-3 flex items-center justify-between gap-3 rounded-md bg-paper2 px-3 py-2 text-[13px]">
        <span class="text-ink2">{{ t('point.error') }}</span>
        <button type="button" data-point-retry class="shrink-0 font-semibold text-forest" @click="load">{{ t('search.retry') }}</button>
      </div>

      <template v-else-if="hit">
        <!-- Сводка (расстояние и координаты) измеряется: под неё подгоняется
             высота открытия мобильной шторки. -->
        <div ref="summaryRef" data-compact-summary class="px-4 pt-3 pb-3 space-y-2">
          <p v-if="distanceText" data-point-distance class="text-[13px] text-ink2 num">{{ distanceText }}</p>
          <p class="flex flex-wrap items-center gap-2 text-[13px]">
            <span data-point-coords class="num">{{ coords.label }}</span>
            <button
              type="button"
              data-copy-coords
              class="w-8 h-8 inline-flex items-center justify-center rounded-md border border-border bg-card text-muted-foreground shadow-xs hover:bg-accent hover:text-foreground transition-colors"
              :aria-label="t('routeDetails.gettingThere.copy')"
              :title="t('routeDetails.gettingThere.copy')"
              @click="copyCoords"
            ><Copy class="w-4 h-4" aria-hidden="true" /></button>
          </p>
          <p v-if="toastMessage" role="status" class="text-[12px] font-semibold text-forest">{{ toastMessage }}</p>
        </div>

        <!-- «Точки рядом»: маршруты в радиусе и точки вокруг (тот же
             provider.nearby, что у поиска); расстояние - от этой точки. -->
        <section v-if="nearbyLoading || nearbyRoutes.length || nearbyPois.length" data-point-nearby class="px-2 pb-4">
          <p v-if="nearbyLoading" class="px-3 py-3 text-xs text-ink2">{{ t('search.loading') }}</p>
          <template v-else>
            <template v-if="nearbyRoutes.length">
              <h3 class="px-2 pt-2 pb-1 text-[11px] font-semibold text-ink2">{{ t('search.nearbyRoutes') }}</h3>
              <SearchHitRow
                v-for="r in nearbyRoutes"
                :key="r.id"
                :hit="r"
                :distance="r.lineDistanceM != null ? { meters: r.lineDistanceM } : null"
                @pick="$emit('open-route', r.routeId)"
              />
            </template>
            <template v-if="nearbyPois.length">
              <h3 class="px-2 pt-2 pb-1 text-[11px] font-semibold text-ink2">{{ t('point.nearbyPoints') }}</h3>
              <SearchHitRow
                v-for="p in shownPois"
                :key="p.id"
                :hit="p"
                :distance="{ meters: haversineM(hit, p) }"
                @pick="$emit('open-point', p.id)"
              />
              <button v-if="hiddenPois > 0" type="button" data-point-more class="px-2 py-1 text-[12px] font-semibold text-forest" @click="poisExpanded = true">
                {{ t('search.more', { n: hiddenPois }) }}
              </button>
            </template>
          </template>
        </section>

        <!-- Место под будущий блок событий тропы (снег, вода, закрытия), см.
             tasks/trail-condition-events.md. Пока пусто. -->
        <slot name="events" />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { ArrowLeft, Copy, X } from "lucide-vue-next";
import SearchHitRow from "../search/SearchHitRow.vue";
import { createApiProvider, fetchPoint } from "../../search/apiProvider";
import { formatDistance, haversineM } from "../../search/geo";
import { kindIcon, kindLabel } from "../../search/kinds";
import { pickLocalizedName } from "../../search/names";
import type { PlaceHit, PoiHit, RouteHit } from "../../search/types";
import { useToast } from "../../composables/useToast";
import { useRoutesStore } from "../../store/routes";
import { useSearchableRoutes } from "../../composables/useSearchableRoutes";
import { useSearchFocusStore } from "../../store/searchFocus";
import { useDistanceOriginStore } from "../../store/distanceOrigin";

const props = defineProps<{ id: string; compact?: boolean }>();
defineEmits<{
  (e: "back"): void;
  (e: "close"): void;
  (e: "open-route", routeId: number): void;
  (e: "open-point", id: string): void;
}>();

const { t, te, locale } = useI18n();
const routesStore = useRoutesStore();
const routes = useSearchableRoutes();
const provider = createApiProvider(() => routes.value);
const searchFocus = useSearchFocusStore();
const originStore = useDistanceOriginStore();
// Точка на карте - через тот же searchFocus, что рисует useSearchLayers.
const FOCUS_OWNER = "point-card";

const state = ref<"loading" | "ready" | "notFound" | "error">("loading");
const hit = ref<PlaceHit | PoiHit | null>(null);
const nearbyRoutes = ref<RouteHit[]>([]);
const nearbyPois = ref<PoiHit[]>([]);
const nearbyLoading = ref(false);
const POIS_SHOWN = 8;
const poisExpanded = ref(false);
const shownPois = computed(() => (poisExpanded.value ? nearbyPois.value : nearbyPois.value.slice(0, POIS_SHOWN)));
const hiddenPois = computed(() => nearbyPois.value.length - shownPois.value.length);

// Ответ по прежнему id (быстрое переключение точек) отбрасывается.
let token = 0;

const loadNearby = async (h: PlaceHit | PoiHit, my: number) => {
  nearbyLoading.value = true;
  try {
    await routesStore.fetch();
    const n = await provider.nearby({ lat: h.lat, lon: h.lon });
    if (my !== token) return;
    nearbyRoutes.value = n.routes;
    nearbyPois.value = n.pois.filter((p) => p.id !== h.id);
  } catch {
    // «Рядом» - дополнение: без него карточка остаётся полезной.
  } finally {
    if (my === token) nearbyLoading.value = false;
  }
};

const load = async () => {
  const my = ++token;
  state.value = "loading";
  hit.value = null;
  nearbyRoutes.value = [];
  nearbyPois.value = [];
  poisExpanded.value = false;
  try {
    const h = await fetchPoint(props.id);
    if (my !== token) return;
    if (!h) {
      state.value = "notFound";
      searchFocus.clearFocus(FOCUS_OWNER);
      return;
    }
    hit.value = h;
    state.value = "ready";
    searchFocus.setFocus({ kind: h.kind, hit: h }, FOCUS_OWNER);
    void loadNearby(h, my);
  } catch {
    if (my === token) state.value = "error";
  }
};
watch(() => props.id, load, { immediate: true });
onBeforeUnmount(() => {
  token++;
  searchFocus.clearFocus(FOCUS_OWNER);
});

const icon = computed(() => (hit.value ? kindIcon(hit.value.osmKind) : null));
const kindWord = computed(() => {
  const k = hit.value ? kindLabel(hit.value.osmKind, t, te) : "";
  return k.charAt(0).toLocaleUpperCase(locale.value) + k.slice(1);
});
// Без названия - тип словом.
const title = computed(() => (hit.value ? pickLocalizedName(hit.value.names, hit.value.name, locale.value) : "") || kindWord.value);
// Высота - только у вершин и перевалов (у остальных OSM её почти не даёт).
const ELEVATION_KINDS = new Set(["peak", "volcano", "hill", "mountain_pass", "saddle"]);
const subtitle = computed(() => {
  const h = hit.value;
  if (!h) return "";
  const kind = h.difficulty ? `${kindWord.value} ${h.difficulty}` : kindWord.value;
  const ele = h.ele != null && ELEVATION_KINDS.has(h.osmKind) ? t("search.elevation", { m: Math.round(h.ele) }) : "";
  return [kind, ele].filter(Boolean).join(" · ");
});

// Расстояние - от той же точки отсчёта, что у строк поиска, с её подписью.
const distanceText = computed(() => {
  const from = originStore.origin;
  if (!hit.value || !from) return "";
  const units = { km: t("units.km"), m: t("units.m") };
  const fromUser = originStore.mode === "user" && searchFocus.userPosition != null;
  return `${formatDistance(haversineM(from, hit.value), units, locale.value)} ${fromUser ? t("search.originUser") : t("search.originCenter")}`;
});

// 6 знаков (~10 см), как «Как добраться до старта» в карточке маршрута.
const coords = computed(() => {
  const lat = Number((hit.value?.lat ?? 0).toFixed(6));
  const lon = Number((hit.value?.lon ?? 0).toFixed(6));
  return { lat, lon, label: `${lat}, ${lon}` };
});
const { message: toastMessage, show: showToast } = useToast();
const copyCoords = async () => {
  try {
    await navigator.clipboard.writeText(coords.value.label);
    showToast(t("routeDetails.gettingThere.copied"));
  } catch {
    showToast(coords.value.label);
  }
};

// Высота шапки и сводки - для высоты открытия мобильной шторки.
const headerRef = ref<HTMLElement | null>(null);
const summaryRef = ref<HTMLElement | null>(null);
const headerHeight = ref(0);
const summaryHeight = ref(0);
const observe = (el: HTMLElement | null, out: typeof headerHeight) => {
  if (!el || typeof ResizeObserver === "undefined") {
    if (!el) out.value = 0;
    return null;
  }
  // border-box (с padding), а не contentRect.
  const o = new ResizeObserver(() => (out.value = el.getBoundingClientRect().height));
  o.observe(el);
  return o;
};
let headerObserver: ResizeObserver | null = null;
let summaryObserver: ResizeObserver | null = null;
watch(headerRef, (el) => {
  headerObserver?.disconnect();
  headerObserver = observe(el, headerHeight);
});
watch(summaryRef, (el) => {
  summaryObserver?.disconnect();
  summaryObserver = observe(el, summaryHeight);
});
onBeforeUnmount(() => {
  headerObserver?.disconnect();
  summaryObserver?.disconnect();
});
const compactHeight = computed(() => headerHeight.value + summaryHeight.value);

defineExpose({ headerHeight, compactHeight });
</script>
